import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Send } from "lucide-react";

import { env } from "@/lib/env";
import { getParticipantSession } from "@/lib/participant-session";
import { getParticipantTelegramStatus } from "@/lib/api/participants";

import { Button } from "@/components/ui/Button";
import { MobileFrame } from "@/components/ui/MobileFrame";

/**
 * V2 spec step 3 — fullscreen "Get notified on Telegram".
 *
 * Sits between ContinueRegistrationPage (where the participant row is
 * materialised, so we have a participantId to link the chat to) and
 * GoalsPage. If the user already linked the bot (typical for the
 * Telegram-OAuth path), we skip forward immediately so the step
 * isn't a dead screen on a refresh.
 *
 * Layout mirrors the mockup: blue TG icon, three bullets describing
 * what we'll DM about, Connect / Not now buttons. Tapping Connect
 * opens the bot deep link; we keep polling the status and auto-advance
 * once the chat lands.
 */
export function ConnectTelegramStepPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = slug ? getParticipantSession(slug) : null;
  const participantId = session?.participantId ?? "";

  const [opened, setOpened] = useState(false);

  const { data: status } = useQuery({
    queryKey: ["telegram-status", participantId],
    queryFn: () => getParticipantTelegramStatus(participantId),
    enabled: Boolean(participantId),
    // Once the user has tapped Connect we poll the row so the screen
    // auto-flips to "Connected" + advances without manual refresh.
    refetchInterval: (q) => (q.state.data?.isLinked ? false : opened ? 3000 : false),
  });

  const isLinked = status?.isLinked ?? false;

  // The user just got back from Telegram — refetch on focus so the
  // poll above doesn't have to wait its full interval.
  useEffect(() => {
    function onFocus() {
      void queryClient.invalidateQueries({
        queryKey: ["telegram-status", participantId],
      });
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [participantId, queryClient]);

  // No session → bounce back to event entry. Happens if the user
  // deep-links into this step without going through registration.
  useEffect(() => {
    if (!slug) return;
    if (!session) navigate(`/e/${slug}`, { replace: true });
  }, [slug, session, navigate]);

  // Already linked: skip the step. Replace so Back doesn't land here
  // when the user later hits the Back button on Goals.
  useEffect(() => {
    if (!slug) return;
    if (isLinked) {
      navigate(`/e/${slug}/goals`, { replace: true });
    }
  }, [slug, isLinked, navigate]);

  if (!session) return null;
  if (isLinked) return null;

  const deepLink = `https://t.me/${env.VITE_TELEGRAM_BOT_USERNAME}?start=${participantId}`;

  return (
    <MobileFrame>
      <div className="flex flex-1 flex-col items-center px-5 py-8">
        <div className="grid h-[70px] w-[70px] place-items-center rounded-full bg-[#2563EB]">
          <Send className="h-9 w-9 text-white" strokeWidth={1.8} />
        </div>

        <h1 className="mt-5 text-center text-[22px] font-bold leading-[1.2] tracking-[-0.01em] text-[#0a0a0a]">
          Get notified on Telegram
        </h1>
        <p className="mt-2 text-center text-[13.5px] leading-relaxed text-[#737373]">
          We'll only ping you about things that matter.
        </p>

        <ul className="mt-6 w-full">
          <Bullet text="New relevant people at the event" />
          <Bullet text="When someone likes you back" />
          <Bullet text="Matches opening before the event" />
        </ul>

        <div className="mt-7 w-full">
          {opened ? (
            <div className="mb-3 flex items-center justify-center gap-2 rounded-[12px] border border-[#bfdbfe] bg-[#eff6ff] px-4 py-3 text-[13px] font-semibold text-[#1d4ed8]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Tap Start in Telegram — this'll update once you do.
            </div>
          ) : null}
          <a
            href={deepLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpened(true)}
            className="flex h-[50px] w-full items-center justify-center gap-2 rounded-[12px] bg-[#2563EB] text-[15px] font-bold text-white no-underline hover:bg-[#1d4ed8]"
          >
            <Send className="h-4 w-4" />
            Connect Telegram
          </a>
          <div className="mt-3">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => navigate(`/e/${slug}/goals`)}
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </MobileFrame>
  );
}

function Bullet({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-3 border-b border-[#e5e5e5] py-3 last:border-b-0">
      <div className="mt-0.5 grid h-[22px] w-[22px] place-items-center rounded-full bg-[#eff3fe]">
        <Check className="h-3 w-3 text-[#2563eb]" strokeWidth={2.4} />
      </div>
      <p className="text-[14px] leading-[1.4] text-[#0a0a0a]">{text}</p>
    </li>
  );
}
